import { ImageUp, Pause, Play, X } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { showDate } from '../../../shared/tree'
import { Avatar } from '@/components/tree/Avatar'
import { albumPhotoUrl, photoSrcSet } from '@/lib/albums'
import { localDay, yearsAgo } from '@/lib/home'
import {
  addedAgo,
  ringKey,
  ringList,
  storyStart,
  useSeeRing,
  useStory,
  type Ring,
  type Rings,
  type StoryPhoto,
} from '@/lib/modern'
import { glowOf, reducedMotion, velocity } from '@/lib/motion'
import { cn } from '@/lib/utils'

const plural = (n: number, one: string) => `${n} ${n === 1 ? one : `${one}s`}`
const ringLabel = (ring: Ring) => (ring.kind === 'day' ? 'On this day' : ring.person.name)

// The rings along the top: On this day, then the people in the photos. A ring in the logo's colours has something you
// haven't seen yet; tapping one plays its photos as a story.
export function RingRow({ rings, onOpen }: { rings: Rings; onOpen: (ring: Ring, from: DOMRect) => void }) {
  const all = ringList(rings)
  if (all.length === 0) return null
  return (
    <ul
      className="scroll-row gap-2 px-4 py-1 sm:px-7 lg:px-[max(2.75rem,calc((100%_-_var(--gallery))/2_+_2.75rem))]"
      aria-label="Rings"
    >
      {all.map((ring) => {
        const fresh = ring.kind === 'day' ? !!rings.day?.fresh : ring.person.fresh
        const label = ringLabel(ring)
        return (
          <li key={ringKey(ring)} className="shrink-0 snap-start">
            <button
              type="button"
              className="mp-ring"
              data-ring={ringKey(ring)}
              aria-label={`${label}${ring.kind === 'day' ? `, ${plural(rings.day!.photos, 'photo')}` : ''}${fresh ? ', new' : ''}`}
              onClick={(e) => onOpen(ring, e.currentTarget.querySelector('.mp-ring-o')!.getBoundingClientRect())}
            >
              <span className="mp-ring-o" data-fresh={fresh || undefined}>
                <span>
                  {ring.kind === 'day' ? (
                    <img
                      src={albumPhotoUrl(rings.day!.cover.ref, 'thumb')}
                      alt=""
                      className="size-full object-cover"
                      decoding="async"
                    />
                  ) : (
                    <Avatar person={ring.person} className="size-full text-2xl shadow-none" />
                  )}
                </span>
              </span>
              <span className={cn('w-full truncate text-xs', fresh ? 'font-semibold' : 'text-muted-foreground')}>
                {label}
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}

// How long a photo stays before the next: longer when there are words to read.
const lasts = (photo: StoryPhoto) => (photo.caption ? 7000 : 5000)

// A ring's photos, one at a time over the whole screen, each lit by its own colours behind it and drifting slowly
// closer. Tap the right side for the next, the left for the one before; hold to stop and look; swipe sideways for
// the next person; pull down to put it away. It opens out of the ring you tapped and closes back into it.
export function Story({
  familyId,
  spaceId,
  ring,
  rings,
  from,
  onRing,
  onClose,
  onOpenPhoto,
}: {
  familyId: string
  spaceId?: string
  ring: Ring
  rings: Ring[]
  from: DOMRect | null
  onRing: (ring: Ring, direction: 1 | -1) => void
  onClose: () => void
  onOpenPhoto: (id: string) => void
}) {
  const story = useStory(familyId, ring, spaceId)
  const photos = useMemo(() => story.data?.photos ?? [], [story.data])
  const key = ringKey(ring)
  // Where in the story you are: at first the first photo not yet seen.
  const [chosen, setChosen] = useState<{ key: string; index: number } | null>(null)
  const index = chosen?.key === key ? chosen.index : storyStart(photos)
  const setIndex = useCallback((i: number) => setChosen({ key, index: i }), [key])
  const [paused, setPaused] = useState(false)
  const [loadedId, setLoadedId] = useState<string | null>(null)
  const root = useRef<HTMLDivElement>(null)
  const bar = useRef<HTMLSpanElement>(null)
  const elapsed = useRef(0)
  const see = useSeeRing(familyId)
  const at = rings.findIndex((r) => ringKey(r) === key)
  const photo = photos[index]
  const loaded = !!photo && loadedId === photo.id

  // Opening a ring: it's no longer new.
  useEffect(() => see(ring), [key]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    elapsed.current = 0
  }, [index, key])

  // It opens out of the ring: a circle the size of the ring that grows to fill the screen.
  useLayoutEffect(() => {
    const el = root.current
    if (!el || !from || reducedMotion()) return
    const x = from.left + from.width / 2
    const y = from.top + from.height / 2
    const end = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y))
    el.animate(
      [{ clipPath: `circle(${from.width / 2}px at ${x}px ${y}px)` }, { clipPath: `circle(${end}px at ${x}px ${y}px)` }],
      { duration: 560, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' },
    )
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const close = useCallback(() => {
    const el = root.current
    const target = document.querySelector(`[data-ring="${CSS.escape(key)}"] .mp-ring-o`)?.getBoundingClientRect()
    if (!el || reducedMotion()) return onClose()
    const shown = target && target.bottom > 0 && target.top < innerHeight
    const x = shown ? target.left + target.width / 2 : innerWidth / 2
    const y = shown ? target.top + target.height / 2 : innerHeight / 2
    const start = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y))
    const moved = el.style.transform
    el.animate(
      [
        { clipPath: `circle(${start}px at ${x}px ${y}px)`, transform: moved || 'none' },
        { clipPath: `circle(${shown ? target.width / 2 : 0}px at ${x}px ${y}px)`, transform: 'none' },
      ],
      { duration: 420, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'forwards' },
    ).onfinish = onClose
  }, [key, onClose])

  const next = useCallback(() => {
    if (index + 1 < photos.length) return setIndex(index + 1)
    const after = rings[at + 1]
    if (after) onRing(after, 1)
    else close()
  }, [index, setIndex, photos.length, rings, at, onRing, close])
  const previous = useCallback(() => {
    if (index > 0) return setIndex(index - 1)
    const before = rings[at - 1]
    if (before) onRing(before, -1)
    else elapsed.current = 0
  }, [index, setIndex, rings, at, onRing])

  // The clock: the bar fills while the photo shows, and stops while it's held, hidden or still arriving.
  useEffect(() => {
    if (!photo) return
    let frame = 0
    let last = performance.now()
    const tick = (now: number) => {
      const dt = now - last
      last = now
      if (!paused && loaded && document.visibilityState === 'visible') elapsed.current += dt
      const p = Math.min(1, elapsed.current / lasts(photo))
      if (bar.current) bar.current.style.transform = `scaleX(${p})`
      if (p >= 1) return next()
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [photo, paused, loaded, next])

  // The next photo is fetched ahead, so it's there when its turn comes.
  useEffect(() => {
    const after = photos[index + 1]
    if (after) new Image().src = albumPhotoUrl(after.ref, 'display')
  }, [photos, index])

  useEffect(() => {
    const keys = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      else if (e.key === 'ArrowRight') next()
      else if (e.key === 'ArrowLeft') previous()
      else if (e.key === ' ') {
        e.preventDefault()
        setPaused((p) => !p)
      }
    }
    addEventListener('keydown', keys)
    return () => removeEventListener('keydown', keys)
  }, [close, next, previous])

  // A finger: a quick tap steps, a rest holds, a pull down closes, a swipe sideways moves to the next person.
  const touch = useRef<{
    x: number
    y: number
    t: number
    points: { t: number; x: number; y: number }[]
    axis: 'x' | 'y' | null
    holdTimer: number
  } | null>(null)
  const onPointerDown = (e: ReactPointerEvent) => {
    if ((e.target as HTMLElement).closest('button')) return
    e.currentTarget.setPointerCapture(e.pointerId)
    const holdTimer = window.setTimeout(() => setPaused(true), 180)
    touch.current = { x: e.clientX, y: e.clientY, t: performance.now(), points: [], axis: null, holdTimer }
  }
  const onPointerMove = (e: ReactPointerEvent) => {
    const t = touch.current
    const el = root.current
    if (!t || !el) return
    const dx = e.clientX - t.x
    const dy = e.clientY - t.y
    t.points.push({ t: performance.now(), x: e.clientX, y: e.clientY })
    if (!t.axis && Math.hypot(dx, dy) > 10) {
      t.axis = Math.abs(dy) > Math.abs(dx) ? 'y' : 'x'
      clearTimeout(t.holdTimer)
      setPaused(true)
    }
    if (t.axis === 'y' && dy > 0) {
      el.style.transform = `translateY(${dy}px) scale(${1 - Math.min(dy, 600) / 1500})`
      el.style.borderRadius = `${Math.min(dy / 6, 28)}px`
    } else if (t.axis === 'x') {
      el.style.transform = `translateX(${dx * 0.5}px) scale(${1 - Math.min(Math.abs(dx), 400) / 3000})`
    }
  }
  const onPointerUp = (e: ReactPointerEvent) => {
    const t = touch.current
    const el = root.current
    touch.current = null
    if (!t || !el) return
    clearTimeout(t.holdTimer)
    const dx = e.clientX - t.x
    const dy = e.clientY - t.y
    const v = velocity(t.points)
    const settle = () => {
      el.animate([{ transform: el.style.transform || 'none' }, { transform: 'none' }], {
        duration: 380,
        easing: 'cubic-bezier(0.34, 1.36, 0.64, 1)',
      })
      el.style.transform = ''
      el.style.borderRadius = ''
    }
    setPaused(false)
    if (t.axis === 'y') {
      if (dy > 120 || v.y > 0.6) return close()
      return settle()
    }
    if (t.axis === 'x') {
      settle()
      if (dx < -70 || v.x < -0.5) {
        const after = rings[at + 1]
        return after ? onRing(after, 1) : close()
      }
      if (dx > 70 || v.x > 0.5) {
        const before = rings[at - 1]
        if (before) onRing(before, -1)
      }
      return
    }
    // A tap, not a rest: the left third goes back, the rest forward.
    if (performance.now() - t.t < 250) {
      if (e.clientX < innerWidth / 3) previous()
      else next()
    }
  }

  const glow = photo ? glowOf(photo.id, null) : null
  const today = localDay()
  return (
    <div
      ref={root}
      className="mp-story"
      role="dialog"
      aria-modal="true"
      aria-label={`${ringLabel(ring)}, photos`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div key={key} className="mp-story-in absolute inset-0">
        {photo && (
          <>
            <img src={albumPhotoUrl(photo.ref, 'thumb')} alt="" aria-hidden className="mp-story-back" />
            <div
              className="mp-story-photo"
              style={{ '--dur': `${lasts(photo) + 1500}ms`, '--play': paused ? 'paused' : 'running' } as CSSProperties}
            >
              <img
                key={photo.id}
                src={albumPhotoUrl(photo.ref, 'medium')}
                srcSet={photoSrcSet(photo, ['medium', 'display'])}
                sizes="100vw"
                alt={photo.caption || 'A photo'}
                draggable={false}
                style={glow ? ({ '--glow': glow } as CSSProperties) : undefined}
                onLoad={(e) => {
                  glowOf(photo.id, e.currentTarget)
                  setLoadedId(photo.id)
                }}
              />
            </div>
          </>
        )}
        <div className="mp-story-shade" />
      </div>

      <div className="absolute inset-x-0 top-0 flex flex-col gap-3 px-3 pt-[calc(var(--safe-top)+0.625rem)]">
        <div className="flex gap-1" aria-hidden>
          {photos.map((p, i) => (
            <span key={p.id} className="mp-bar">
              <span ref={i === index ? bar : undefined} style={{ transform: `scaleX(${i < index ? 1 : 0})` }} />
            </span>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {ring.kind === 'person' ? (
            <Avatar person={ring.person} className="size-9 text-base shadow-none" />
          ) : (
            <span className="grid size-9 place-items-center rounded-full bg-viewer-foreground/15 text-xs font-semibold">
              {Number(today.slice(8))}
            </span>
          )}
          <div className="flex min-w-0 flex-1 flex-col leading-tight">
            <span className="truncate font-semibold">{ringLabel(ring)}</span>
            {photo && (
              <span className="truncate text-sub text-viewer-muted">
                {ring.kind === 'day' && photo.takenOn
                  ? `${showDate(photo.takenOn)} · ${yearsAgo(Number(photo.takenOn.slice(0, 4)), today)}`
                  : `Added by ${photo.by} ${addedAgo(photo.createdAt)}`}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setPaused((p) => !p)}
            className="grid size-10 place-items-center rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-viewer-foreground/50"
            aria-label={paused ? 'Play' : 'Pause'}
          >
            {paused ? <Play className="size-5" aria-hidden /> : <Pause className="size-5" aria-hidden />}
          </button>
          <button
            type="button"
            onClick={close}
            className="grid size-10 place-items-center rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-viewer-foreground/50"
            aria-label="Close"
          >
            <X className="size-6" aria-hidden />
          </button>
        </div>
      </div>

      {photo && (
        <div className="absolute inset-x-0 bottom-0 flex flex-col items-start gap-3 px-5 pb-[calc(var(--safe-bottom)+1.25rem)]">
          {photo.caption && <p className="max-w-xl text-lg leading-snug font-medium text-balance">{photo.caption}</p>}
          <div className="flex w-full items-center justify-between gap-3">
            <span className="text-sub text-viewer-muted">
              {ring.kind === 'person' && photo.takenOn ? showDate(photo.takenOn) : ''}
            </span>
            <button
              type="button"
              onClick={() => onOpenPhoto(photo.id)}
              className="flex h-10 items-center gap-2 rounded-full bg-viewer-foreground/15 px-4 text-sm font-medium backdrop-blur-md outline-none focus-visible:ring-[3px] focus-visible:ring-viewer-foreground/50"
            >
              <ImageUp className="size-4" aria-hidden /> Open photo
            </button>
          </div>
        </div>
      )}
      {story.data && photos.length === 0 && (
        <p className="absolute inset-0 grid place-items-center text-viewer-muted">No photos here right now.</p>
      )}
    </div>
  )
}
